import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    login as authLogin,
    verify2FA as authVerify2FA,
    logout as authLogout,
    getCurrentUser,
    isAuthenticated as checkAuth,
    AdminUser,
    AuthResponse
} from '../services/authService';
import api, { getProfile } from '../services/api';

interface AuthContextType {
    user: AdminUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<AuthResponse>;
    verify2FA: (tempToken: string, code: string) => Promise<AuthResponse>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getActiveRegion = (userAssigned?: string): 'PK' | 'UAE' => {
    if (userAssigned && (userAssigned.toUpperCase() === 'PK' || userAssigned.toUpperCase() === 'UAE')) {
        return userAssigned.toUpperCase() as 'PK' | 'UAE';
    }
    const port = typeof window !== 'undefined' ? window.location.port : '';
    if (port === '3003' || port === '3001' || import.meta.env.VITE_REGION_CODE === 'UAE') {
        return 'UAE';
    }
    return 'PK';
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AdminUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const isAuth = checkAuth();

            if (isAuth) {
                const currentUser = getCurrentUser();
                if (currentUser) {
                    setUser(currentUser);
                }

                try {
                    const profileRes = await getProfile();
                    const profileUser = profileRes.data?.data?.user || profileRes.data?.user || profileRes.data;
                    if (profileUser) {
                        const name = profileUser.name || currentUser?.name || '';
                        const parts = name.trim().split(/\s+/);
                        const assignedRegion = getActiveRegion(profileUser.assignedRegion || currentUser?.assignedRegion);
                        const defaultHub = assignedRegion === 'UAE' ? 'Dubai' : 'Karachi';
                        const normalizedUser: AdminUser = {
                            id: profileUser.id || profileUser._id || currentUser?.id || '',
                            email: profileUser.email || currentUser?.email || '',
                            name: name || undefined,
                            firstName: profileUser.firstName || parts[0] || currentUser?.firstName || '',
                            lastName: profileUser.lastName || parts.slice(1).join(' ') || currentUser?.lastName || '',
                            role: profileUser.role || currentUser?.role || 'admin',
                            isAdmin: profileUser.isAdmin ?? (profileUser.role === 'admin' || profileUser.role === 'super_admin'),
                            phone: profileUser.phone || currentUser?.phone,
                            assignedRegion,
                            city: assignedRegion === 'UAE' ? 'Dubai' : (profileUser.city || profileUser.assignedCity || currentUser?.city || currentUser?.assignedCity || defaultHub),
                            hub: assignedRegion === 'UAE' ? 'Dubai' : (profileUser.hub || profileUser.assignedHub || currentUser?.hub || currentUser?.assignedHub || defaultHub),
                            location: assignedRegion === 'UAE' ? 'Dubai' : (profileUser.location || currentUser?.location || defaultHub),
                            assignedCity: assignedRegion === 'UAE' ? 'Dubai' : (profileUser.assignedCity || currentUser?.assignedCity || defaultHub),
                            assignedHub: assignedRegion === 'UAE' ? 'Dubai' : (profileUser.assignedHub || currentUser?.assignedHub || defaultHub),
                        };
                        setUser(normalizedUser);
                        localStorage.setItem('admin_user', JSON.stringify(normalizedUser));
                    }
                } catch (err) {
                    console.warn('[AuthContext] Profile hydration failed, using cached credentials:', err);
                }
            }

            setIsLoading(false);
        };

        initAuth();
    }, []);

    const login = async (email: string, password: string): Promise<AuthResponse> => {
        setIsLoading(true);
        try {
            const response = await authLogin(email, password);

            // If login did not require 2FA, set user directly
            if (response.user) {
                const u = { ...response.user };
                if (u.name && (!u.firstName || !u.lastName)) {
                    const parts = u.name.trim().split(/\s+/);
                    u.firstName = parts[0] || '';
                    u.lastName = parts.slice(1).join(' ') || '';
                }
                const assignedRegion = getActiveRegion(u.assignedRegion);
                const defaultHub = assignedRegion === 'UAE' ? 'Dubai' : 'Karachi';
                u.assignedRegion = assignedRegion;
                u.city = assignedRegion === 'UAE' ? 'Dubai' : (u.city || u.assignedCity || defaultHub);
                u.hub = assignedRegion === 'UAE' ? 'Dubai' : (u.hub || u.assignedHub || defaultHub);
                u.location = assignedRegion === 'UAE' ? 'Dubai' : (u.location || defaultHub);
                setUser(u);
            }

            return response;
        } catch (error) {
            console.error('[AuthContext] Login error:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const verify2FA = async (tempToken: string, code: string): Promise<AuthResponse> => {
        setIsLoading(true);
        try {
            const response = await authVerify2FA(tempToken, code);
            if (response.user) {
                const u = { ...response.user };
                if (u.name && (!u.firstName || !u.lastName)) {
                    const parts = u.name.trim().split(/\s+/);
                    u.firstName = parts[0] || '';
                    u.lastName = parts.slice(1).join(' ') || '';
                }
                const assignedRegion = getActiveRegion(u.assignedRegion);
                const defaultHub = assignedRegion === 'UAE' ? 'Dubai' : 'Karachi';
                u.assignedRegion = assignedRegion;
                u.city = assignedRegion === 'UAE' ? 'Dubai' : (u.city || u.assignedCity || defaultHub);
                u.hub = assignedRegion === 'UAE' ? 'Dubai' : (u.hub || u.assignedHub || defaultHub);
                u.location = assignedRegion === 'UAE' ? 'Dubai' : (u.location || defaultHub);
                setUser(u);
            }
            return response;
        } catch (error) {
            console.error('[AuthContext] 2FA verification error:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        authLogout();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            verify2FA,
            logout
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};